// Global Instructions Rule Applied!
//
// Notices section of the capital call form (capital call notices, stage 3).
//
// Issuing a call generates one notice per investor and stores it; this
// section is where they are reviewed before anything is sent. Per investor
// it shows the stored notice's status and offers View (the stored PDF),
// Download, and Regenerate; "Download all" hands back one archive.
//
// Before issue nothing is stored: each row offers Preview, a live rendering
// of the notice as it would be generated now. After issue a stored notice is
// a fixed document, so when the data behind it moves (the fund's terms, the
// wording, the bank account, the call's name or purpose) it is marked "Out
// of date" rather than silently changing - Regenerate replaces it, and
// Preview shows what that would produce. Reverting the call supersedes its
// notices; issuing it again produces the next version ("Revised").
//
// The notice purpose lives here because it is about the notice, and stays
// editable after issue (the rest of an issued call is read-only).
//
// The generation runs on the server in the background, so the hook re-reads
// the list while any notice is pending and the section shows the progress.
//
// Sending (stage 7): "Send notices" shows what would go out - per account its
// notices and its recipients on the Capital Calls topic - and creates a DRAFT
// email, which opens in the compose form to be reviewed and queued there.
// Nothing is sent from this section. While the call has a draft notice email
// the button opens it instead. The Sent column shows, per notice, whether it
// went and in which email; "Revised" marks a notice that replaces one the
// investor was sent.
//
// Additional documents (stage 8): an investor flagged on its commitment gets
// a second file with the notice - the ILPA workbook. It is stored on the same
// notice, so one status, one version and one "sent" cover both. The row shows
// a tag for it and a download button (a workbook is not shown in the file
// viewer); "Download all" and the notice email carry it with the notice.

import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  Alert,
  Button,
  Checkbox,
  Modal,
  Popconfirm,
  Progress,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DownloadOutlined, MailOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { InputManager } from '../../../core/components/InputManager/index.ts'
import { FieldConfig, FieldTypeEnum } from '../../../core/components/InputManager/types.ts'
import NumberText from '../../../core/components/NumberText.tsx'
import { useRefreshOnActivate } from '../../../core/hooks/useRefreshOnActivate.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { getSignedUrl } from '../../../core/services/filesService.ts'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { API_ROUTES } from '../../../core/utils/endpoints.ts'
import { archiveNameFor, createZip, uniqueArchiveName } from '../../../core/utils/zip.ts'
import type { ZipEntry } from '../../../core/utils/zip.ts'
import { store } from '../../../state_manager/store.ts'
import { addTab } from '../../../store/slices/tabSlice'
import { ReportPreviewPane } from '../../document_reports/components/ReportPreviewPane.tsx'
import { fetchReportPreview, saveBlob } from '../../document_reports/services/documentReportsService.ts'
import { useCapitalCallNotices } from '../hooks/useCapitalCallNotices.ts'
import { createNoticeCampaign, fetchNoticeCampaignPlan } from '../services/capitalCallNoticesService.ts'
import {
  CapitalCallNoticeRow,
  CapitalCallNotices,
  NoticeSendState,
  NoticeStatusCode
} from '../../../state_manager/data_models/capital_call_notices.ts'
import {
  CapitalCallNoticeCampaignPlan,
  NoticePlanAccount
} from '../../../state_manager/data_models/capital_call_notice_campaign_plan.ts'

const { Text } = Typography

const DATE_FORMAT = 'MM/DD/YYYY'
const PDF_MIME = 'application/pdf'
const PURPOSE_FIELD_KEY = 'capital_calls__notice_purpose'
// InputManager builds `${backend}/${patchEndpoint}`: the route path without
// its leading slash.
const PURPOSE_PATCH = API_ROUTES.CAPITAL_CALLS.NOTICE_PURPOSE_PATCH.path.replace(/^\//, '')
// the tab module of the email compose form (/email/forms/full/:id)
const EMAIL_MODULE = 'email'
const RECORD_LINK_CLASS =
  'cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'

interface NoticesSectionProps {
  capitalCallId: string
  /** Changes whenever the form re-hydrates from the server. */
  version: unknown
}

const investorName = (row: CapitalCallNoticeRow) => row.investors__name || row.investors__legal_name || 'Investor'

const isGenerated = (row: CapitalCallNoticeRow) =>
  row.capital_call_notices__is_current && row.capital_call_notice_statuses__code === NoticeStatusCode.generated

const isPending = (row: CapitalCallNoticeRow) =>
  row.capital_call_notices__is_current &&
  row.capital_call_notice_statuses__code === NoticeStatusCode.pending &&
  !row.prog__is_stalled

/** The stored PDF of a row, when it has one. */
const storedFile = (row: CapitalCallNoticeRow) =>
  row.file_urls_capital_call_notices__file_url
    ? {
        path: row.file_urls_capital_call_notices__file_url,
        name: row.file_urls_capital_call_notices__file_name || `${investorName(row)}.pdf`
      }
    : null

/** The additional documents stored with a row's notice (the ILPA workbook). */
const supplementFiles = (row: CapitalCallNoticeRow) =>
  (row.supplement_files ?? []).map((f) => ({
    id: f.file_urls_capital_call_notices__id,
    path: f.file_urls_capital_call_notices__file_url,
    name: f.file_urls_capital_call_notices__file_name,
    label: f.notice_templates__label || f.notice_templates__code,
    shortLabel: f.notice_templates__short_label || f.notice_templates__label || f.notice_templates__code
  }))

const isWorkbookName = (name: string) => /\.xlsx$/i.test(name)

/** What a supplement file is called on a button or in a count. */
const supplementNoun = (name: string) => (isWorkbookName(name) ? 'workbook' : 'document')

/** The header Tags: what the stored notices of the call add up to. Exported
 *  for the test. */
export function noticeSummaryTags(notices: CapitalCallNotices): Array<{ key: string; color?: string; text: string }> {
  const s = notices.prog__summary
  if (!notices.call.counts_as_called || s.total === 0) return []
  const tags: Array<{ key: string; color?: string; text: string }> = []
  if (s.generated > 0) tags.push({ key: 'generated', color: 'green', text: `${s.generated} of ${s.total} generated` })
  if (s.pending > 0) tags.push({ key: 'pending', color: 'blue', text: `${s.pending} generating` })
  if (s.failed > 0) tags.push({ key: 'failed', color: 'red', text: `${s.failed} failed` })
  if (s.stalled > 0) tags.push({ key: 'stalled', color: 'gold', text: `${s.stalled} stalled` })
  if (s.stale > 0) tags.push({ key: 'stale', color: 'gold', text: `${s.stale} out of date` })
  if (s.not_generated > 0) tags.push({ key: 'missing', text: `${s.not_generated} not generated` })
  if (s.sent > 0) tags.push({ key: 'sent', color: 'green', text: `${s.sent} of ${s.total} sent` })
  return tags
}

/** "2 To, 1 Cc" for an account of the send plan; '' when it has nobody. */
export function planRecipientsText(account: NoticePlanAccount): string {
  const to = account.recipients.filter((r) => r.role !== 'cc').length
  const cc = account.recipients.length - to
  return [to > 0 ? `${to} To` : '', cc > 0 ? `${cc} Cc` : ''].filter(Boolean).join(', ')
}

/** The Notices cell of the send plan: a bare count while the account's
 *  notices are all it gets, "2 notices, 1 workbook" once additional documents
 *  go with them. Exported for the test. */
export function planNoticesText(account: NoticePlanAccount): string {
  const notices = account.notices.length
  const extras = account.notices.flatMap((n) => n.supplement_files ?? [])
  if (extras.length === 0) return String(notices)
  const noun = extras.every((f) => isWorkbookName(f.file_urls_capital_call_notices__file_name))
    ? 'workbook'
    : 'document'
  return `${notices} notice${notices === 1 ? '' : 's'}, ${extras.length} ${noun}${extras.length === 1 ? '' : 's'}`
}

// re-exported for the test; the statement runs' "Download all" shares it
export { uniqueArchiveName }

async function fetchStoredFile(path: string): Promise<Blob> {
  const signed = await getSignedUrl(path, getAccessToken(store.getState().auth))
  const response = await fetch(signed.signed_url)
  if (!response.ok) throw new Error(`The stored file could not be downloaded (${response.status})`)
  return response.blob()
}

interface PreviewState {
  investor: string
  blobUrl: string | null
  pageCount: number | null
  loading: boolean
  error: string | null
}

interface SendState {
  plan: CapitalCallNoticeCampaignPlan | null
  loading: boolean
  error: string | null
  onlyUnsent: boolean
}

export default function NoticesSection({ capitalCallId, version }: NoticesSectionProps) {
  const dispatch = useDispatch()
  const { openFormInTab } = useTabManagerContext()
  const { notices, loading, error, refetch, generate, generating } = useCapitalCallNotices(capitalCallId, version)
  const activateRef = useRefreshOnActivate(refetch)

  const [downloadingAll, setDownloadingAll] = useState(false)
  const [busyRow, setBusyRow] = useState<string | null>(null)
  const [preview, setPreview] = useState<PreviewState | null>(null)
  const [send, setSend] = useState<SendState | null>(null)
  const [creatingEmail, setCreatingEmail] = useState(false)

  const issued = notices?.call.counts_as_called ?? false
  const problems = notices?.prog__problems ?? []
  const rows = useMemo(() => notices?.data ?? [], [notices])
  const summary = notices?.prog__summary
  const downloadable = useMemo(() => rows.filter((r) => isGenerated(r) && storedFile(r)), [rows])
  const runInFlight = (summary?.pending ?? 0) > 0

  const closePreview = useCallback(() => {
    setPreview((current) => {
      if (current?.blobUrl) URL.revokeObjectURL(current.blobUrl)
      return null
    })
  }, [])

  /** A live rendering of the notice as it would be generated now. */
  const handlePreview = useCallback(
    async (row: CapitalCallNoticeRow) => {
      const reportCode = notices?.settings.template_code
      if (!reportCode) {
        showErrorToast('No notice layout is active')
        return
      }
      const investor = investorName(row)
      setPreview({ investor, blobUrl: null, pageCount: null, loading: true, error: null })
      const result = await fetchReportPreview(
        {
          reportCode,
          params: { capital_call_id: capitalCallId, fund_commitment_id: row.fund_commitments__id }
        },
        getAccessToken(store.getState().auth)
      )
      setPreview((current) => {
        // closed, or another row's preview opened, while this one rendered
        if (!current || current.investor !== investor || !current.loading) {
          if (result.blobUrl) URL.revokeObjectURL(result.blobUrl)
          return current
        }
        return {
          investor,
          blobUrl: result.blobUrl ?? null,
          pageCount: result.pageCount ?? null,
          loading: false,
          error: result.success ? null : result.error || 'Failed to render the notice'
        }
      })
    },
    [capitalCallId, notices]
  )

  /** The stored PDF, in a file-viewer tab (the house way to open a file). */
  const handleView = useCallback(
    async (row: CapitalCallNoticeRow) => {
      const file = storedFile(row)
      if (!file) return
      try {
        const signed = await getSignedUrl(file.path, getAccessToken(store.getState().auth))
        const tabKey = `file-viewer-${file.path.replace(/[^a-zA-Z0-9]/g, '-')}-${Date.now()}`
        dispatch(
          addTab({
            id: tabKey,
            key: tabKey,
            title: file.name,
            path: `/file-viewer/${tabKey}`,
            type: 'file-viewer',
            module: 'file-viewer',
            closeable: true,
            fileUrl: signed.signed_url,
            fileName: file.name
          })
        )
      } catch (e) {
        showErrorToast(e instanceof Error ? e.message : 'Failed to open the notice')
      }
    },
    [dispatch]
  )

  /** Save one stored file of a row: its notice, or an additional document. */
  const handleDownload = useCallback(
    async (row: CapitalCallNoticeRow, file: { path: string; name: string } | null = storedFile(row)) => {
      if (!file) return
      setBusyRow(row.fund_commitments__id)
      try {
        saveBlob(await fetchStoredFile(file.path), file.name)
      } catch (e) {
        showErrorToast(e instanceof Error ? e.message : `Failed to download ${file.name}`)
      } finally {
        setBusyRow(null)
      }
    },
    []
  )

  const handleDownloadAll = useCallback(async () => {
    setDownloadingAll(true)
    try {
      const entries: ZipEntry[] = []
      const failed: string[] = []
      const used = new Set<string>()
      let loneType = PDF_MIME
      for (const row of downloadable) {
        const notice = storedFile(row)
        if (!notice) continue
        // the notice, then the documents that go with it
        for (const file of [notice, ...supplementFiles(row)]) {
          try {
            const blob = await fetchStoredFile(file.path)
            loneType = blob.type || PDF_MIME
            entries.push({ name: uniqueArchiveName(file.name, used), bytes: new Uint8Array(await blob.arrayBuffer()) })
          } catch {
            failed.push(file === notice ? investorName(row) : `${investorName(row)} (${file.name})`)
          }
        }
      }
      if (entries.length === 0) {
        showErrorToast('None of the notices could be downloaded')
        return
      }
      if (entries.length === 1 && failed.length === 0) {
        // a lone notice is a plain file - no one wants to unzip one file
        saveBlob(new Blob([entries[0].bytes], { type: loneType }), entries[0].name)
      } else {
        saveBlob(
          createZip(entries),
          archiveNameFor(
            entries.map((e) => e.name),
            'Capital Call Notices'
          )
        )
      }
      if (failed.length > 0) showErrorToast(`Not in the download: ${failed.join(', ')}`)
    } finally {
      setDownloadingAll(false)
    }
  }, [downloadable])

  const openEmail = useCallback(
    (id: string, name?: string | null) => openFormInTab(EMAIL_MODULE, id, name ?? null),
    [openFormInTab]
  )

  /** Read what "Send notices" would create. When some notices were already
   *  sent, the plan is narrowed to the investors not yet sent: sending the
   *  same notice twice is the choice to make on purpose. */
  const loadPlan = useCallback(
    async (onlyUnsent: boolean | null) => {
      setSend((current) => ({
        plan: current?.plan ?? null,
        loading: true,
        error: null,
        onlyUnsent: onlyUnsent ?? false
      }))
      const token = getAccessToken(store.getState().auth)
      let narrowed = onlyUnsent ?? false
      let res = await fetchNoticeCampaignPlan(capitalCallId, narrowed, token)
      if (onlyUnsent === null && res.data && res.data.prog__summary.already_sent > 0) {
        narrowed = true
        res = await fetchNoticeCampaignPlan(capitalCallId, true, token)
      }
      setSend((current) =>
        current === null
          ? null
          : {
              plan: res.data,
              loading: false,
              error: res.data ? null : res.error || 'Failed to read what would be sent',
              onlyUnsent: narrowed
            }
      )
    },
    [capitalCallId]
  )

  const handleCreateEmail = useCallback(async () => {
    if (!send?.plan) return
    setCreatingEmail(true)
    try {
      const res = await createNoticeCampaign(capitalCallId, send.onlyUnsent, getAccessToken(store.getState().auth))
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to create the notice email')
        return
      }
      if (res.data.prog__sync_error) {
        showErrorToast(
          `The email was created but could not be filled: ${res.data.prog__sync_error} Use "Refresh from capital call" on its Recipients tab.`
        )
      } else {
        toast.success('Notice email drafted — review it and queue it from the email form')
      }
      setSend(null)
      openEmail(res.data.email_campaigns__id, res.data.email_campaigns__name)
      await refetch()
    } finally {
      setCreatingEmail(false)
    }
  }, [capitalCallId, send, openEmail, refetch])

  const runGenerate = useCallback(
    async (row: CapitalCallNoticeRow | null) => {
      if (row) setBusyRow(row.fund_commitments__id)
      try {
        const res = await generate(row ? [row.fund_commitments__id] : null)
        if (res.error || res.count == null) {
          showErrorToast(res.error || 'Failed to generate the notices')
          return
        }
        toast.success(`Generating ${res.count} notice${res.count === 1 ? '' : 's'}`)
      } finally {
        if (row) setBusyRow(null)
      }
    },
    [generate]
  )

  const purposeFields = useMemo(
    (): FieldConfig[] => [
      {
        key: PURPOSE_FIELD_KEY,
        label: 'Notice Purpose',
        type: FieldTypeEnum.TEXTAREA,
        value: notices?.call.capital_calls__notice_purpose ?? null,
        editable: true,
        rows: 2,
        autoSize: { minRows: 2, maxRows: 6 },
        patchEndpoint: PURPOSE_PATCH,
        labelTooltip:
          'One or two sentences on what the proceeds are for, printed on every notice after the opening paragraph. It can be changed after the call is issued; regenerate the notices to print the change.'
      }
    ],
    [notices]
  )

  const statusCell = useCallback(
    (row: CapitalCallNoticeRow) => {
      const code = row.capital_call_notice_statuses__code
      if (!row.capital_call_notices__id || !code) {
        return issued && row.prog__on_call ? <Tag>Not generated</Tag> : <Text type='secondary'>—</Text>
      }
      const version = row.capital_call_notices__version ?? 1
      return (
        <div>
          <Space size={4} wrap>
            {row.prog__is_stalled ? (
              <Tooltip title='Generation did not finish: the server stopped while this notice was being produced. Regenerate it.'>
                <Tag color='gold'>Stalled</Tag>
              </Tooltip>
            ) : (
              <Tag color={row.capital_call_notice_statuses__tag_color ?? undefined}>
                {row.capital_call_notice_statuses__label ?? code}
              </Tag>
            )}
            {version > 1 ? (
              <Tooltip title='This notice replaces one the investor was already sent: it prints as a Revised Capital Call Notice.'>
                <Tag color='blue'>Revised · v{version}</Tag>
              </Tooltip>
            ) : null}
            {row.prog__is_stale ? (
              <Tooltip title="The fund's terms, the notice wording, the bank account, the call or this investor's figures changed after the notice was generated. Preview shows the notice as it would be generated now; Regenerate replaces the stored one.">
                <Tag color='gold'>Out of date</Tag>
              </Tooltip>
            ) : null}
            {supplementFiles(row).map((file) => (
              <Tooltip key={file.id} title={`Sent with the notice: ${file.label}`}>
                <Tag color='blue'>{file.shortLabel}</Tag>
              </Tooltip>
            ))}
          </Space>
          {code === NoticeStatusCode.failed && row.capital_call_notices__error_message ? (
            <div className='text-xs text-red-600 mt-1'>{row.capital_call_notices__error_message}</div>
          ) : null}
        </div>
      )
    },
    [issued]
  )

  const sentCell = useCallback(
    (row: CapitalCallNoticeRow) => {
      const state = row.prog__send_state
      if (!state) return <Text type='secondary'>—</Text>
      const tag =
        state === NoticeSendState.sent ? (
          <Tag color='green'>
            Sent{row.prog__sent_at ? ` ${dayjs(row.prog__sent_at).format(DATE_FORMAT)}` : ''}
          </Tag>
        ) : state === NoticeSendState.failed ? (
          <Tooltip title="The email finished without this investor's message going out. Open the email to see why.">
            <Tag color='red'>Send failed</Tag>
          </Tooltip>
        ) : state === NoticeSendState.draft ? (
          <Tag color='blue'>In draft email</Tag>
        ) : (
          <Tag color='blue'>Sending</Tag>
        )
      const campaignId = row.email_campaigns__id
      return (
        <div>
          {tag}
          {campaignId ? (
            <div>
              <button
                type='button'
                className={`${RECORD_LINK_CLASS} text-xs`}
                onClick={() => openEmail(campaignId, row.email_campaigns__name)}
              >
                {row.email_campaigns__name || 'Notice email'}
              </button>
            </div>
          ) : null}
        </div>
      )
    },
    [openEmail]
  )

  const columns = useMemo(
    (): ColumnsType<CapitalCallNoticeRow> => [
      {
        title: 'Investor',
        key: 'investor',
        render: (_: unknown, row) => (
          <span>
            {investorName(row)}
            {!row.prog__on_call ? <Text type='secondary'> (no longer on the call)</Text> : null}
          </span>
        )
      },
      {
        title: 'Amount Due',
        key: 'amount',
        width: 150,
        align: 'right',
        render: (_: unknown, row) => <NumberText value={row.prog__call_total} />
      },
      { title: 'Status', key: 'status', render: (_: unknown, row) => statusCell(row) },
      {
        title: 'Generated',
        key: 'generated',
        width: 110,
        render: (_: unknown, row) =>
          row.capital_call_notices__generated_at ? dayjs(row.capital_call_notices__generated_at).format(DATE_FORMAT) : '—'
      },
      { title: 'Sent', key: 'sent', width: 220, render: (_: unknown, row) => sentCell(row) },
      {
        title: '',
        key: 'actions',
        width: 380,
        align: 'right',
        render: (_: unknown, row) => {
          const file = storedFile(row)
          const busy = busyRow === row.fund_commitments__id
          // a stored notice that still matches the live data IS the preview
          const offerPreview = row.prog__on_call && (!isGenerated(row) || row.prog__is_stale)
          const offerGenerate = issued && row.prog__on_call && !isPending(row)
          return (
            <Space size={4}>
              {offerPreview ? (
                <Button size='small' onClick={() => handlePreview(row)}>
                  Preview
                </Button>
              ) : null}
              {file ? (
                <Button size='small' onClick={() => handleView(row)}>
                  View
                </Button>
              ) : null}
              {file ? (
                <Button size='small' loading={busy && !generating} onClick={() => handleDownload(row)}>
                  Download
                </Button>
              ) : null}
              {file
                ? supplementFiles(row).map((extra) => (
                    <Tooltip key={extra.id} title={`Download ${extra.label}`}>
                      <Button size='small' disabled={busy} onClick={() => handleDownload(row, extra)}>
                        {supplementNoun(extra.name) === 'workbook' ? 'Workbook' : extra.shortLabel}
                      </Button>
                    </Tooltip>
                  ))
                : null}
              {offerGenerate ? (
                <Button
                  size='small'
                  loading={busy && generating}
                  disabled={problems.length > 0 || (generating && !busy)}
                  onClick={() => runGenerate(row)}
                >
                  {row.capital_call_notices__is_current ? 'Regenerate' : 'Generate'}
                </Button>
              ) : null}
            </Space>
          )
        }
      }
    ],
    [
      statusCell,
      sentCell,
      busyRow,
      issued,
      generating,
      problems.length,
      handlePreview,
      handleView,
      handleDownload,
      runGenerate
    ]
  )

  // a blocked action says why (the reasons themselves are in the Alert below)
  const blockedReason =
    problems.length > 0 ? "The notices cannot be produced until the fund's terms below are complete." : undefined

  // one draft at a time is the working copy: the button opens it
  const draftEmail = notices?.campaigns.find((c) => c.email_campaigns__status === 'draft')
  const sendBlockedReason = notices?.prog__send_blocked_reason ?? undefined

  const headerActions =
    notices && notices.call.prog__notice_applicable ? (
      <>
        {noticeSummaryTags(notices).map((tag) => (
          <Tag key={tag.key} color={tag.color}>
            {tag.text}
          </Tag>
        ))}
        {issued ? (
          <Button
            size='small'
            icon={<DownloadOutlined />}
            loading={downloadingAll}
            disabled={downloadable.length === 0}
            onClick={handleDownloadAll}
          >
            Download all
          </Button>
        ) : null}
        {issued && draftEmail ? (
          <Tooltip title='This call has a draft notice email. Review it and queue it from the email form.'>
            <Button
              size='small'
              icon={<MailOutlined />}
              onClick={() => openEmail(draftEmail.email_campaigns__id, draftEmail.email_campaigns__name)}
            >
              Open notice email
            </Button>
          </Tooltip>
        ) : issued ? (
          <Tooltip title={sendBlockedReason}>
            <Button
              size='small'
              icon={<MailOutlined />}
              disabled={!!sendBlockedReason || generating || runInFlight}
              onClick={() => void loadPlan(null)}
            >
              Send notices
            </Button>
          </Tooltip>
        ) : null}
        {issued && rows.some((r) => r.prog__on_call) ? (
          (summary?.not_generated ?? 0) === (summary?.total ?? 0) ? (
            <Tooltip title={blockedReason}>
              <Button
                size='small'
                loading={generating && busyRow === null}
                disabled={problems.length > 0 || generating}
                onClick={() => runGenerate(null)}
              >
                Generate
              </Button>
            </Tooltip>
          ) : (
            <Popconfirm
              title='Regenerate every notice?'
              description='Each stored notice is replaced by a new one built from the current data. The earlier files are kept as superseded.'
              okText='Regenerate'
              disabled={problems.length > 0 || generating || runInFlight}
              onConfirm={() => runGenerate(null)}
            >
              <Tooltip title={blockedReason}>
                <Button
                  size='small'
                  loading={generating && busyRow === null}
                  disabled={problems.length > 0 || generating || runInFlight}
                >
                  Regenerate all
                </Button>
              </Tooltip>
            </Popconfirm>
          )
        ) : null}
      </>
    ) : null

  let body: React.ReactNode
  if (!notices) {
    body = loading ? (
      <div className='flex justify-center py-6'>
        <Spin />
      </div>
    ) : error ? (
      <Alert type='error' showIcon message='Failed to load the notices' description={error} />
    ) : null
  } else if (!notices.call.prog__notice_applicable) {
    body = (
      <Text type='secondary'>
        This call is an intercompany pass-through: limited partners are not sent a notice for it.
      </Text>
    )
  } else {
    const total = summary?.total ?? 0
    const done = total - (summary?.pending ?? 0)
    body = (
      <>
        {problems.length > 0 ? (
          <Alert
            className='mb-3'
            type='warning'
            showIcon
            message='The notices cannot be produced yet'
            description={problems.map((p) => (
              <div key={p}>{p}</div>
            ))}
          />
        ) : null}
        <InputManager
          inputMode='read'
          labelPosition='inline'
          enableAutoPatch
          patchId={capitalCallId}
          recordId={capitalCallId}
          currentRecord={notices.call}
          fields={purposeFields}
          onPatchSuccess={() => refetch()}
          fieldWrapping
        />
        <div className='mt-3'>
          {runInFlight ? (
            <div className='mb-2 flex items-center gap-3'>
              <Progress
                size='small'
                className='!mb-0 max-w-xs'
                percent={total > 0 ? Math.round((done / total) * 100) : 0}
                showInfo={false}
                status='active'
              />
              <Text type='secondary'>
                Generating notices — {done} of {total} done
              </Text>
            </div>
          ) : null}
          {!issued ? (
            <Text type='secondary' className='block mb-2'>
              The notices are generated and stored when the call is issued. Until then, Preview shows each one as it
              would be generated now.
            </Text>
          ) : null}
          {rows.length === 0 ? (
            <Text type='secondary'>No investor has an amount on this call yet — run Compute.</Text>
          ) : (
            <Table<CapitalCallNoticeRow>
              size='small'
              pagination={false}
              rowKey='fund_commitments__id'
              columns={columns}
              dataSource={rows}
            />
          )}
        </div>
      </>
    )
  }

  const plan = send?.plan ?? null
  const planAccounts = plan?.accounts ?? []
  const noRecipient = planAccounts.filter((a) => a.recipients.length === 0)
  const onlyCc = planAccounts.filter((a) => a.recipients.length > 0 && !a.prog__has_to)
  const emailed = planAccounts.filter((a) => a.recipients.length > 0)
  const withoutAccount = plan?.prog__summary.notices_without_account ?? 0
  const createBlocked = plan?.prog__blocked_reason ?? null

  const planColumns: ColumnsType<NoticePlanAccount> = [
    { title: 'Account', key: 'account', render: (_: unknown, a) => a.accounts__name || 'Account' },
    {
      title: 'Notices',
      key: 'notices',
      width: planAccounts.some((a) => a.notices.some((n) => (n.supplement_files ?? []).length > 0)) ? 170 : 80,
      align: 'right',
      render: (_: unknown, a) => planNoticesText(a)
    },
    {
      title: 'Amount Due',
      key: 'amount',
      width: 150,
      align: 'right',
      render: (_: unknown, a) => <NumberText value={a.prog__amount_due} />
    },
    {
      title: 'Recipients',
      key: 'recipients',
      width: 150,
      render: (_: unknown, a) =>
        a.recipients.length === 0 ? (
          <Tag color='gold'>None</Tag>
        ) : (
          <Tooltip
            title={a.recipients.map((r) => (
              <div key={r.contacts__id}>
                {r.role === 'cc' ? 'Cc' : 'To'}: {r.contacts__email}
              </div>
            ))}
          >
            <span>{planRecipientsText(a)}</span>
          </Tooltip>
        )
    }
  ]

  return (
    <div ref={activateRef}>
      <FormSection title='Notices' titleColor='purple' scrollable={false} headerActions={headerActions}>
        {body}
      </FormSection>
      <Modal
        title='Send notices'
        open={send !== null}
        onCancel={() => setSend(null)}
        onOk={handleCreateEmail}
        okText='Create email'
        confirmLoading={creatingEmail}
        okButtonProps={{ disabled: !plan || send?.loading || !!createBlocked || emailed.length === 0 }}
        width={760}
        destroyOnHidden
      >
        {!plan ? (
          send?.error ? (
            <Alert type='error' showIcon message='Failed to read what would be sent' description={send.error} />
          ) : (
            <div className='flex justify-center py-6'>
              <Spin />
            </div>
          )
        ) : (
          <Spin spinning={send?.loading ?? false}>
            {createBlocked ? <Alert className='mb-3' type='warning' showIcon message={createBlocked} /> : null}
            <div className='mb-3'>
              A draft email is created for {emailed.length} account{emailed.length === 1 ? '' : 's'}. Each account
              receives only its own notices, with any additional documents its investors are flagged for. Nothing is
              sent yet: the email opens for you to review, and you queue it
              from there.
            </div>
            {plan.prog__summary.already_sent > 0 ? (
              <Checkbox
                className='mb-3'
                checked={send?.onlyUnsent ?? false}
                onChange={(e) => void loadPlan(e.target.checked)}
              >
                Only investors not yet sent this version ({plan.prog__summary.already_sent} already sent)
              </Checkbox>
            ) : null}
            {noRecipient.length > 0 || onlyCc.length > 0 || withoutAccount > 0 ? (
              <Alert
                className='mb-3'
                type='warning'
                showIcon
                message={
                  noRecipient.length > 0
                    ? 'These investors get no email. Add a contact to the Capital Calls topic.'
                    : 'Some notices need attention before they can go.'
                }
                description={
                  <>
                    {noRecipient.length > 0 ? <div>{noRecipient.map((a) => a.accounts__name).join(', ')}</div> : null}
                    {onlyCc.length > 0 ? (
                      <div>
                        Only Cc recipients, no To: {onlyCc.map((a) => a.accounts__name).join(', ')}. Set a To on the
                        email&apos;s Recipients tab.
                      </div>
                    ) : null}
                    {withoutAccount > 0 ? (
                      <div>
                        {withoutAccount} notice{withoutAccount === 1 ? ' belongs' : 's belong'} to an investor with no
                        account and cannot be emailed.
                      </div>
                    ) : null}
                  </>
                }
              />
            ) : null}
            {planAccounts.length === 0 ? (
              <Text type='secondary'>No investor is left to send to.</Text>
            ) : (
              <Table<NoticePlanAccount>
                size='small'
                pagination={false}
                rowKey='accounts__id'
                columns={planColumns}
                dataSource={planAccounts}
                scroll={{ y: 320 }}
              />
            )}
          </Spin>
        )}
      </Modal>
      <Modal
        title={preview ? `Notice preview — ${preview.investor}` : 'Notice preview'}
        open={preview !== null}
        onCancel={closePreview}
        footer={null}
        width={920}
        destroyOnHidden
      >
        <div style={{ height: '72vh' }}>
          <ReportPreviewPane
            blobUrl={preview?.blobUrl ?? null}
            pageCount={preview?.pageCount ?? null}
            loading={preview?.loading ?? false}
            error={preview?.error ?? null}
            emptyText='Rendering the notice…'
          />
        </div>
      </Modal>
    </div>
  )
}
